/**
 * FERP PDF Comparison View (DRR-079)
 *
 * Side-by-side dual PDF viewer for comparing document versions.
 * Uses two independent react-pdf Document + Page instances.
 */

import { useState, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Box, Group, Stack, Text, Paper, ActionIcon, Tooltip, Loader, Center } from '@mantine/core';
import { IconChevronLeft, IconChevronRight, IconZoomIn, IconZoomOut } from '@tabler/icons-react';

interface ComparisonViewProps {
  /** URL for the left (original/older) PDF */
  leftUrl: string;
  /** URL for the right (new/current) PDF */
  rightUrl: string;
  /** Labels */
  leftLabel?: string;
  rightLabel?: string;
}

export function ComparisonView({
  leftUrl,
  rightUrl,
  leftLabel = 'Original',
  rightLabel = 'Current',
}: ComparisonViewProps) {
  const [leftPages, setLeftPages] = useState(0);
  const [rightPages, setRightPages] = useState(0);
  const [leftPage, setLeftPage] = useState(1);
  const [rightPage, setRightPage] = useState(1);
  const [scale, setScale] = useState(0.8);
  const [syncPages, setSyncPages] = useState(true);

  const handleLeftPageChange = useCallback(
    (page: number) => {
      setLeftPage(Math.max(1, Math.min(page, leftPages)));
      if (syncPages) setRightPage(Math.max(1, Math.min(page, rightPages)));
    },
    [leftPages, rightPages, syncPages],
  );

  const handleRightPageChange = useCallback(
    (page: number) => {
      setRightPage(Math.max(1, Math.min(page, rightPages)));
      if (syncPages) setLeftPage(Math.max(1, Math.min(page, leftPages)));
    },
    [leftPages, rightPages, syncPages],
  );

  const renderSide = (
    url: string,
    label: string,
    numPages: number,
    currentPage: number,
    onLoadSuccess: (args: { numPages: number }) => void,
    onPageChange: (page: number) => void,
  ) => (
    <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
      <Paper withBorder p="xs" style={{ borderBottom: 'none' }}>
        <Group justify="space-between">
          <Text size="sm" fw={500}>
            {label}
          </Text>
          <Group gap={4}>
            <ActionIcon
              variant="subtle"
              size="xs"
              disabled={currentPage <= 1}
              onClick={() => onPageChange(currentPage - 1)}
            >
              <IconChevronLeft size={14} />
            </ActionIcon>
            <Text size="xs">
              {currentPage} / {numPages || '?'}
            </Text>
            <ActionIcon
              variant="subtle"
              size="xs"
              disabled={currentPage >= numPages}
              onClick={() => onPageChange(currentPage + 1)}
            >
              <IconChevronRight size={14} />
            </ActionIcon>
          </Group>
        </Group>
      </Paper>
      <Paper
        withBorder
        style={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          backgroundColor: 'var(--mantine-color-gray-1)',
        }}
      >
        <Document
          file={url}
          onLoadSuccess={onLoadSuccess}
          loading={
            <Center py="xl">
              <Loader size="sm" />
            </Center>
          }
        >
          <Page pageNumber={currentPage} scale={scale} renderTextLayer renderAnnotationLayer />
        </Document>
      </Paper>
    </Stack>
  );

  return (
    <Stack gap={0} style={{ height: '100%' }}>
      {/* Shared controls */}
      <Paper withBorder p="xs">
        <Group justify="center" gap="xs">
          <Tooltip label="Zoom out">
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={() => setScale((s) => Math.max(0.25, s - 0.1))}
            >
              <IconZoomOut size={16} />
            </ActionIcon>
          </Tooltip>
          <Text size="xs">{Math.round(scale * 100)}%</Text>
          <Tooltip label="Zoom in">
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={() => setScale((s) => Math.min(3, s + 0.1))}
            >
              <IconZoomIn size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Paper>

      {/* Side-by-side viewers */}
      <Group gap={0} style={{ flex: 1, overflow: 'hidden' }} align="stretch" wrap="nowrap">
        {renderSide(leftUrl, leftLabel, leftPages, leftPage, ({ numPages }) => setLeftPages(numPages), handleLeftPageChange)}
        <Box style={{ width: 2, backgroundColor: 'var(--mantine-color-gray-4)' }} />
        {renderSide(rightUrl, rightLabel, rightPages, rightPage, ({ numPages }) => setRightPages(numPages), handleRightPageChange)}
      </Group>
    </Stack>
  );
}
